import ChecklistHeader from "./ChecklistHeader.js";
import ChecklistItem from "./ChecklistItem.js";
import ChecklistSkeleton from "./ChecklistSkeleton.js";
import ErrorBoundary from "../ErrorBoundary.js";
import * as ChecklistContext from "./ChecklistContext.js";
import SuspenseWithDelay from "../SuspenseWithDelay.js";
import { cn } from "../../utils/cn.js";
import { useIsGummy } from "../../hooks/useGummyTheme.js";

const Checklist = () => {
  const props = ChecklistContext.useChecklistContext();
  const { queryClient, checklist } = props;
  const isGummy = useIsGummy();

  const { items } = checklist;

  const scrollToUnchecked = async (
    { checklist_item_id, status }: { checklist_item_id: number; status: 1 | 2 },
    e: PointerEvent
  ) => {
    if (!items) return;
    // if it was unchecked, don't scroll
    if (status !== 2) return;
    const lastChangedIndex = items?.findIndex(
      (item) => item.id === checklist_item_id
    );
    // if there's no next item, don't scroll
    if (items[lastChangedIndex + 1] === undefined) return;
    const nextItems = items.slice(lastChangedIndex + 1);
    const isAnythingChecked = nextItems.some((item) => item.status === 2);
    if (isAnythingChecked) return;

    // function that checks all items after the last changed item
    // and returns the first item that is unchecked

    const unchecked = items[lastChangedIndex + 1] ?? items[lastChangedIndex];
    if (!unchecked || !items) return;
    const index = items.indexOf(unchecked);
    if (index > lastChangedIndex) {
      const el = document.querySelector(
        `[items-checklist-item="${unchecked.id}"]`
      );
      if (el) {
        const clickedCheckboxPosition = e.pageY;
        const nextCheckmark = el.querySelector(".checkmark");
        if (nextCheckmark) {
          const nextCheckmarkPosition = nextCheckmark.getBoundingClientRect().y;
          // const
          // wait 500ms
          await new Promise((resolve) => setTimeout(resolve, 500));
          const scrollTarget = document.querySelector("#main-content");
          scrollTarget?.scrollBy({
            top: nextCheckmarkPosition - clickedCheckboxPosition + 15,
            // top: 500,
            behavior: "smooth"
          });
        }
      }
    }
  };

  const checkedItems = items?.filter((item) => item.status === 2);

  if (isGummy) {
    return (
      <div className="mx-auto max-w-4xl">
        <div
          className={cn(
            "shadow-card-deep",
            "overflow-hidden rounded-surface-xl bg-white dark:bg-mono-900"
          )}
        >
          <div className="h-1.5 rounded-t-gummy-xl" />
          <div className="mobile-pref-card p-5 pt-0">
            <ChecklistHeader
              itemCount={checklist?.items.length ?? 0}
              completeCount={checkedItems.length}
              checklist={checklist}
            />
            <ul className="grid gap-y-5 md:gap-y-6">
              {items?.map((checklistItem, i) => (
                <li
                  key={checklistItem.id}
                  items-checklist-item={`${checklistItem.id}`}
                >
                  <ChecklistItem
                    queryClient={queryClient}
                    checkedItems={checkedItems ?? []}
                    handleChecked={scrollToUnchecked}
                    checklist={checklist}
                    itemNumber={i + 1}
                    item={checklistItem}
                    totalItems={checklist?.items?.length || 0}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="h-[40vh]" />
      </div>
    );
  }

  return (
    <div className={`mx-auto max-w-4xl`}>
      <div className="dark:bg-mono-950 sticky  top-0 z-10 ">
        <ChecklistHeader
          itemCount={checklist?.items.length ?? 0}
          completeCount={checkedItems.length}
          checklist={checklist}
        />
        <div
          className={`bg-mono-200 dark:bg-mono-800/50 pointer-events-none absolute top-[100%] z-10 h-0.5 w-full`}
        />
      </div>
      <div className="h-8" />
      <ul className="grid gap-y-8 md:gap-y-12">
        {items?.map((checklistItem, i) => {
          return (
            <li
              key={checklistItem.id}
              items-checklist-item={`${checklistItem.id}`}
            >
              <ChecklistItem
                queryClient={queryClient}
                checkedItems={checkedItems ?? []}
                handleChecked={scrollToUnchecked}
                checklist={checklist}
                itemNumber={i + 1}
                item={checklistItem}
                totalItems={checklist?.items?.length || 0}
              />
            </li>
          );
        })}
        <li className={`h-[50vh] w-full`} />
      </ul>
    </div>
  );
};

Checklist.useChecklist = ChecklistContext.useChecklistContext;
Checklist.Provider = ({
  children,
  ...props
}: ChecklistContext.ChecklistVariables & { children: React.ReactNode }) => {
  return (
    <SuspenseWithDelay fallback={<ChecklistSkeleton />}>
      <ErrorBoundary>
        <ChecklistContext.ChecklistProvider {...props}>
          {children}
        </ChecklistContext.ChecklistProvider>
      </ErrorBoundary>
    </SuspenseWithDelay>
  );
};
Checklist.Skeleton = ChecklistSkeleton;
export default Checklist;
